import React, { useEffect, useState, useCallback } from "react";
import { Trash2, Filter, Copy, AlertTriangle, ImageIcon, HardDrive } from "lucide-react";
import { api, resolveUrl } from "../lib/api";
import { toast } from "sonner";

function humanSize(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function GalleryPanel() {
  const [files, setFiles] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("all"); // all | used | unused
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fRes, sRes] = await Promise.all([
        api.get("/admin/gallery"),
        api.get("/admin/gallery/stats"),
      ]);
      setFiles(fRes.data);
      setStats(sRes.data);
    } catch (err) {
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (f, force = false) => {
    const msg = f.usage_count > 0
      ? `This file is used in ${f.usage_count} place(s). Delete anyway? (Places that reference it will show a broken image until replaced.)`
      : "Delete this unused file?";
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/gallery/${f.id}`, { params: { force } });
      toast.success("File deleted");
      load();
    } catch (err) {
      if (err.response?.status === 409 && !force) {
        if (window.confirm(err.response.data.detail + "\n\nForce delete?")) {
          remove(f, true);
        }
      } else {
        toast.error(err.response?.data?.detail || "Delete failed");
      }
    }
  };

  const copyUrl = (f) => {
    navigator.clipboard.writeText(f.url);
    toast.success("URL copied");
  };

  const shown = files.filter((f) => {
    if (filter === "used") return f.usage_count > 0;
    if (filter === "unused") return f.usage_count === 0;
    return true;
  });

  return (
    <div data-testid="admin-gallery">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h2 className="font-display text-3xl text-[#1F3864] mb-1">Image Gallery</h2>
          <p className="text-sm text-gray-600">All files uploaded via the admin console. Delete unused files to free up space.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white border-l-4 border-[#C00000] p-5">
          <ImageIcon className="w-5 h-5 text-[#1F3864] mb-2" />
          <div className="text-2xl font-semibold text-[#1F3864]" style={{ fontFamily: "'DM Sans', sans-serif" }}>{stats.total_files ?? "—"}</div>
          <div className="text-xs tracking-[0.2em] uppercase text-gray-500 mt-1">Total Files</div>
        </div>
        <div className="bg-white border-l-4 border-[#1F3864] p-5">
          <div className="text-2xl font-semibold text-[#1F3864]" style={{ fontFamily: "'DM Sans', sans-serif" }}>{stats.used_files ?? "—"}</div>
          <div className="text-xs tracking-[0.2em] uppercase text-gray-500 mt-1">In Use</div>
        </div>
        <div className="bg-white border-l-4 border-[#C00000] p-5">
          <AlertTriangle className="w-5 h-5 text-[#C00000] mb-2" />
          <div className="text-2xl font-semibold text-[#1F3864]" style={{ fontFamily: "'DM Sans', sans-serif" }}>{stats.unused_files ?? "—"}</div>
          <div className="text-xs tracking-[0.2em] uppercase text-gray-500 mt-1">Unused</div>
        </div>
        <div className="bg-white border-l-4 border-[#1F3864] p-5">
          <HardDrive className="w-5 h-5 text-[#1F3864] mb-2" />
          <div className="text-2xl font-semibold text-[#1F3864]" style={{ fontFamily: "'DM Sans', sans-serif" }}>{humanSize(stats.total_size_bytes)}</div>
          <div className="text-xs tracking-[0.2em] uppercase text-gray-500 mt-1">Total Size</div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 mb-6">
        <Filter className="w-4 h-4 text-gray-500" />
        {[
          { key: "all", label: "All" },
          { key: "used", label: "In Use" },
          { key: "unused", label: "Unused" },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase border ${
              filter === f.key ? "bg-[#1F3864] text-white border-[#1F3864]" : "border-gray-300 text-gray-700 hover:border-[#1F3864]"
            }`}
            data-testid={`gallery-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto text-xs text-gray-500">{shown.length} file{shown.length === 1 ? "" : "s"}</div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-500">Loading...</div>
      ) : shown.length === 0 ? (
        <div className="bg-white border border-gray-200 p-16 text-center text-gray-500">
          <ImageIcon className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          No files {filter !== "all" && `(${filter})`} yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {shown.map((f) => (
            <div key={f.id} className="bg-white border border-gray-200 group" data-testid={`gallery-item-${f.id}`}>
              <button onClick={() => setPreview(f)} className="block w-full aspect-square bg-gray-100 overflow-hidden relative">
                <img src={resolveUrl(f.url)} alt={f.original_filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                {f.usage_count === 0 && (
                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-[#C00000] text-white text-[9px] tracking-[0.2em] uppercase flex items-center gap-1">
                    <AlertTriangle className="w-2.5 h-2.5" /> Unused
                  </div>
                )}
                {f.usage_count > 0 && (
                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-[#1F3864]/90 text-white text-[9px] tracking-[0.2em] uppercase">
                    × {f.usage_count}
                  </div>
                )}
              </button>
              <div className="p-3 space-y-1">
                <div className="text-xs font-medium text-[#1F3864] truncate" title={f.original_filename}>{f.original_filename || "untitled"}</div>
                <div className="text-[10px] text-gray-500 flex justify-between">
                  <span>{humanSize(f.size)}</span>
                  <span>{new Date(f.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex gap-1 pt-1">
                  <button onClick={() => copyUrl(f)} className="flex-1 p-1.5 border border-gray-200 hover:border-[#1F3864] text-xs" title="Copy URL" data-testid={`gallery-copy-${f.id}`}>
                    <Copy className="w-3 h-3 inline" />
                  </button>
                  <button onClick={() => remove(f)} className="flex-1 p-1.5 border border-gray-200 hover:border-[#C00000] hover:text-[#C00000] text-xs" title="Delete" data-testid={`gallery-delete-${f.id}`}>
                    <Trash2 className="w-3 h-3 inline" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-200 flex justify-between items-center">
              <div>
                <div className="font-display text-lg text-[#1F3864]">{preview.original_filename}</div>
                <div className="text-xs text-gray-500">{humanSize(preview.size)} · {preview.content_type}</div>
              </div>
              <button onClick={() => setPreview(null)} className="text-gray-500 hover:text-[#C00000] text-2xl leading-none">×</button>
            </div>
            <div className="p-5 bg-gray-50">
              <img src={resolveUrl(preview.url)} alt="" className="max-w-full max-h-[60vh] mx-auto" />
            </div>
            <div className="p-5 space-y-3">
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-1">URL</div>
                <div className="flex gap-2">
                  <code className="flex-1 px-3 py-2 bg-[#F5F5F5] text-xs font-mono break-all">{preview.url}</code>
                  <button onClick={() => copyUrl(preview)} className="px-3 py-2 border border-gray-300 hover:border-[#1F3864] text-xs"><Copy className="w-3 h-3" /></button>
                </div>
              </div>
              {preview.used_in && preview.used_in.length > 0 && (
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Used in ({preview.used_in.length})</div>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {preview.used_in.map((u, i) => (
                      <li key={i} className="flex justify-between border-b border-gray-100 pb-1.5">
                        <span className="capitalize">{u.collection.replace("_", " ")}</span>
                        <span className="text-[#1F3864] font-medium">{u.item_name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(!preview.used_in || preview.used_in.length === 0) && (
                <div className="p-3 border border-[#C00000]/30 bg-[#C00000]/5 text-sm text-[#C00000] flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> This file is not used anywhere — safe to delete.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
